import { useEffect, useMemo, useState } from "react";
import { FiFileText, FiInfo, FiX } from "react-icons/fi";

const isValidHsn = (value) => /^\d{8}$/.test(value);

export default function HsnCodeModal({
  open,
  onClose,
  onSave,
  hsnCode = null,
}) {
  const [form, setForm] = useState({
    code: "",
    description: "",
    isActive: true,
  });
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");
    setForm({
      code: hsnCode?.code || "",
      description: hsnCode?.description || "",
      isActive: hsnCode?.isActive !== false,
    });
  }, [open, hsnCode]);

  const cleanedCode = useMemo(
    () => form.code.replace(/\D/g, "").slice(0, 8),
    [form.code],
  );

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isValidHsn(cleanedCode)) {
      setError("Enter a valid 8-digit Indian HSN code.");
      return;
    }

    if (!form.description.trim()) {
      setError("Enter the official product description for this HSN code.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      await onSave({
        code: cleanedCode,
        description: form.description.trim(),
        isActive: form.isActive,
      });

      onClose();
    } catch (requestError) {
      const message = requestError?.details?.message || requestError?.message;

      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to save HSN code.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="mx-auto flex min-h-full max-w-2xl items-center py-6">
        <div className="w-full overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 sm:px-8 sm:py-6">
            <div>
              <div className="flex items-center gap-2 text-primary-700">
                <FiFileText />
                <span className="text-xs font-bold uppercase tracking-[0.14em]">
                  Indian GST catalogue
                </span>
              </div>

              <h2 className="mt-2 text-2xl font-bold text-gray-900">
                {hsnCode ? "Edit HSN Code" : "Add HSN Code"}
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Add the correct goods classification before assigning it to a
                product.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600 transition hover:bg-gray-200 disabled:opacity-50"
            >
              <FiX size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6 p-6 sm:p-8">
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              )}

              <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="flex gap-3">
                  <FiInfo className="mt-0.5 shrink-0 text-amber-700" />
                  <p>
                    Use the official HSN classification for the actual product.
                    Use the complete 8-digit tariff item code used for your
                    product. This keeps product imports, invoices, and GST
                    reports consistent.
                  </p>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  HSN Code *
                </label>

                <input
                  type="text"
                  inputMode="numeric"
                  maxLength="8"
                  placeholder="Example: 61091000"
                  value={form.code}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      code: event.target.value.replace(/\D/g, "").slice(0, 8),
                    }))
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 px-4 font-mono text-lg font-semibold tracking-wider outline-none transition focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                  required
                />

                <p className="mt-2 text-xs text-gray-500">
                  Required: exactly 8 numeric digits.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Goods Description *
                </label>

                <textarea
                  rows="4"
                  required
                  placeholder="Example: Articles of jewellery and parts thereof, of precious metal..."
                  value={form.description}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      description: event.target.value,
                    }))
                  }
                  className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 outline-none transition focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </div>


              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-700">
                  Catalogue preview
                </p>

                <p className="mt-2 font-mono text-2xl font-bold text-blue-950">
                  {cleanedCode || "HSN CODE"}
                </p>

                <p className="mt-2 text-sm text-blue-800">
                  {form.description.trim() ||
                    "Goods description will appear here."}
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Status
                </label>

                <select
                  value={form.isActive ? "ACTIVE" : "INACTIVE"}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      isActive: event.target.value === "ACTIVE",
                    }))
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none transition focus:border-primary-700"
                >
                  <option value="ACTIVE">
                    Active — available for products
                  </option>
                  <option value="INACTIVE">
                    Inactive — retained for history only
                  </option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="h-11 rounded-xl border border-gray-200 px-6 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSaving}
                className="h-11 rounded-xl bg-primary-700 px-6 font-semibold text-white transition hover:bg-primary-800 disabled:opacity-60"
              >
                {isSaving
                  ? "Saving..."
                  : hsnCode
                    ? "Update HSN Code"
                    : "Save HSN Code"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
